import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch,
  faClock,
  faChartLine,
  faArrowTrendUp,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import RunCard from '../components/history/RunCard';
import api from '../lib/api';
import { estimateCalories, DEFAULT_WEIGHT_KG } from '../lib/calories';
import { useDebounce } from '../hooks/useDebounce';
import { logger } from '../lib/logger';

// Helper function
const formatDuration = (interval) => {
  if (!interval) return '-';
  const match = interval.match(/(\d+):(\d+):(\d+)/);
  if (match) {
    const hours = parseInt(match[1]);
    const mins = parseInt(match[2]);
    const secs = parseInt(match[3]);
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  return interval;
};

const RunHistory = () => {
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.user);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [sortBy, setSortBy] = useState('date');
  const [filterDistance, setFilterDistance] = useState('all');
  const [filterDate, setFilterDate] = useState('all');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [allRuns, setAllRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const isMountedRef = useRef(true);

  const handleShare = async (run) => {
    setOpenMenuId(null);
    if (navigator.share) {
      try {
        await navigator.share({
          title: run.title,
          text: `Check out my run: ${run.distance}km in ${run.duration}`,
        });
      } catch (err) {
        // User canceled share - ignore
      }
    }
  };

  const handlePostToFeed = (run) => {
    setOpenMenuId(null);
    const draftPost = {
      type: 'run',
      title: run.title,
      content: `Great ${run.distance}km run!`,
      distance: run.distance.toString(),
      time: run.duration,
      pace: run.pace
    };
    
    localStorage.setItem('draftPost', JSON.stringify(draftPost));
    
    navigate('/feed');
  };

  // Fetch runs from API
  const fetchRuns = useCallback(async () => {
    if (!authUser?.id) {
      setIsLoading(false);
      return;
    }

    try {
      const [runs, settings] = await Promise.all([
        api.runs.list(null, 'desc'),
        api.profiles.getSettings(authUser.id).catch(() => null),
      ]);

      const userWeight = settings?.weight_kg ?? DEFAULT_WEIGHT_KG;

      const formattedRuns = (runs || []).map(run => ({
        id: run.id,
        title: run.title,
        date: run.run_date,
        time: run.run_time || '',
        distance: parseFloat(run.distance_km) || 0,
        duration: formatDuration(run.duration),
        rawDuration: run.duration,
        pace: run.pace || '-',
        calories: estimateCalories(parseFloat(run.distance_km) || 0, run.duration, userWeight)
      }));

      if (isMountedRef.current) {
        setAllRuns(formattedRuns);
        setError(null);
      }
    } catch (err) {
      logger.warn('Error fetching runs (non-blocking):', err);
      if (isMountedRef.current) {
        setError('Failed to load runs. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [authUser?.id]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchRuns();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchRuns]);

  // Filter and search logic
  const filteredRuns = useMemo(() => {
    let filtered = allRuns.filter(run => 
      run.title?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );

    if (filterDistance !== 'all') {
      filtered = filtered.filter(run => {
        if (filterDistance === 'short') return run.distance < 5;
        if (filterDistance === 'medium') return run.distance >= 5 && run.distance < 10;
        if (filterDistance === 'long') return run.distance >= 10;
        return true;
      });
    }

    if (filterDate !== 'all') {
      const now = new Date();
      
      filtered = filtered.filter(run => {
        const date = new Date(run.date);
        if (filterDate === 'week') {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return date >= weekAgo;
        }
        if (filterDate === 'month') {
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return date >= monthAgo;
        }
        if (filterDate === 'year') {
          const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          return date >= yearAgo;
        }
        return true;
      });
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'distance':
          return b.distance - a.distance;
        case 'pace':
          return a.pace.localeCompare(b.pace);
        case 'duration':
          return b.duration.localeCompare(a.duration);
        default: // date
          return new Date(b.date) - new Date(a.date);
      }
    });

    return filtered;
  }, [allRuns, debouncedSearchTerm, filterDistance, filterDate, sortBy]);

  if (isLoading) {
    return (
      <div className="p-4 max-w-6xl mx-auto flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-primary-pink animate-spin mb-4" />
          <p className="text-gray-600">Loading runs...</p>
        </div>
      </div>
    );
  }

  if (error && allRuns.length === 0) {
    return (
      <div className="p-4 max-w-6xl mx-auto flex items-center justify-center min-h-[60vh]">
        <div className="scrapbook-container text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">
            Couldn't load runs
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setIsLoading(true);
              fetchRuns();
            }}
            className="px-6 py-2 bg-gradient-vibe text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-semibold mb-2 bg-gradient-vibe bg-clip-text text-transparent">
          Run History
        </h1>
        <p className="text-gray-600 text-body">
          Browse and track your running journey with detailed insights
        </p>
      </div>

      <div className="scrapbook-container mb-6">
        <div className="flex flex-row gap-4">
          <div className="flex-1 relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search runs by title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-pink focus:border-transparent transition-all"
            />
          </div>

          <div className="flex gap-2 overflow-x-auto">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-primary-pink bg-white transition-colors"
            >
              <option value="date">Sort by Date</option>
              <option value="distance">Sort by Distance</option>
              <option value="pace">Sort by Pace</option>
              <option value="duration">Sort by Duration</option>
            </select>

            <select
              value={filterDistance}
              onChange={(e) => setFilterDistance(e.target.value)}
              className="px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-primary-pink bg-white transition-colors"
            >
              <option value="all">All Distances</option>
              <option value="short">Short (&lt;5km)</option>
              <option value="medium">Medium (5-10km)</option>
              <option value="long">Long (&gt;10km)</option>
            </select>

            <select
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-primary-pink bg-white transition-colors"
            >
              <option value="all">All Time</option>
              <option value="week">Past Week</option>
              <option value="month">Past Month</option>
              <option value="year">Past Year</option>
            </select>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredRuns.length} of {allRuns.length} runs
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { 
            label: 'Total Distance', 
            value: `${filteredRuns.reduce((sum, run) => sum + run.distance, 0).toFixed(1)}km`,
            icon: faChartLine,
            color: 'text-primary-pink'
          },
          { 
            label: 'Total Runs', 
            value: filteredRuns.length,
            icon: faArrowTrendUp,
            color: 'text-secondary-blue'
          },
          { 
            label: 'Avg Distance', 
            value: filteredRuns.length > 0 ? `${(filteredRuns.reduce((sum, run) => sum + run.distance, 0) / filteredRuns.length).toFixed(1)}km` : '0km',
            icon: faChartLine,
            color: 'text-primary-yellow'
          },
          { 
            label: 'Total Calories', 
            value: `${filteredRuns.reduce((sum, run) => sum + run.calories, 0).toLocaleString()}`,
            icon: faClock,
            color: 'text-secondary-pink'
          }
        ].map((stat) => (
          <div
            key={stat.label}
            className="scrapbook-container text-center p-4"
          >
            <FontAwesomeIcon icon={stat.icon} className={`w-6 h-6 ${stat.color} mx-auto mb-2`} />
            <div className="font-heading font-bold text-xl text-gray-900 mb-1">
              {stat.value}
            </div>
            <div className="text-xs text-gray-500">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        {allRuns.length === 0 ? (
          <div className="scrapbook-container text-center py-12">
            <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">
              No runs yet
            </h3>
            <p className="text-gray-600">
              Start tracking your runs to see them here!
            </p>
          </div>
        ) : filteredRuns.length === 0 ? (
          <div className="scrapbook-container text-center py-12">
            <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">
              No runs found
            </h3>
            <p className="text-gray-600">
              Try adjusting your search terms or filters
            </p>
          </div>
        ) : (
          filteredRuns.map((run, index) => (
            <RunCard 
              key={run.id} 
              run={run} 
              index={index}
              openMenuId={openMenuId}
              setOpenMenuId={setOpenMenuId}
              handleShare={handleShare}
              handlePostToFeed={handlePostToFeed}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default RunHistory;
